import Hls from "hls.js";

// do not change the order of this import
import "media-chrome/lang/fr";
import "../media_chrome/vi";

import "media-chrome";
import "media-chrome/menu";

import { playerSignatureFragLoader } from "../hls_player_signature";

let autoplay = false;

let hideControlsTimeout = null;

let canAutoplay = null;

// PostHog when it has been set up: always in the embed, in the app only when a key is configured.
function playbackAnalytics() {
  const posthog = window.posthog;
  return posthog && posthog.__loaded ? posthog : null;
}

function roundedSeconds(value) {
  return Number.isFinite(value) ? Math.round(value) : null;
}

function formatClock(seconds) {
  if (!Number.isFinite(seconds)) return "?";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mmss = `${h ? String(m).padStart(2, "0") : m}:${String(s).padStart(2, "0")}`;
  return h ? `${h}:${mmss}` : mmss;
}

function hlsResponseStatus(data) {
  const status = Number(
    data?.response?.code ?? data?.response?.status ?? data?.response?.statusCode,
  );
  return Number.isFinite(status) && status > 0 ? status : null;
}

// The URL a failed hls.js request was sent to. The response URL first: `frag.url` can already
// hold a newer URL by the time the error fires.
function hlsErrorUrl(data) {
  return (
    data?.response?.url ||
    data?.networkDetails?.responseURL ||
    data?.context?.url ||
    data?.frag?.url ||
    data?.url ||
    ""
  );
}

// Whether an hls.js error came from loading a video chunk rather than the playlist.
function isChunkRequestError(data) {
  const responseUrl =
    data?.frag?.url ||
    data?.frag?.relurl ||
    data?.response?.url ||
    data?.networkDetails?.responseURL ||
    "";

  return (
    data?.details === Hls.ErrorDetails.FRAG_LOAD_ERROR ||
    data?.details === Hls.ErrorDetails.FRAG_LOAD_TIMEOUT ||
    Boolean(data?.frag) ||
    String(responseUrl).includes(".ts")
  );
}

function exitAnyFullscreen(video) {
  try {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else if (document.webkitFullscreenElement) document.webkitExitFullscreen?.();
    // iPhone: the video element itself goes fullscreen, outside the page
    if (video?.webkitDisplayingFullscreen) video.webkitExitFullscreen?.();
  } catch (_) {}
}

export function setCanAutoplay(value) {
  canAutoplay = value;
}

export function getCanAutoplay() {
  return canAutoplay;
}

export function hidePlayerUI() {
  const controller = document.querySelector("media-controller");
  if (!controller) return;
  const subtitle = controller.querySelector("#subtitle");
  const controlsMiddle = controller.querySelector("#sm-controls-middle");
  const controlBar = controller.querySelector("media-control-bar");
  if (subtitle) subtitle.style.display = "none";
  if (controlsMiddle) controlsMiddle.style.display = "none";
  if (controlBar) controlBar.style.display = "none";
}

export function showPlayerUI() {
  const controller = document.querySelector("media-controller");
  if (!controller) return;
  const subtitle = controller.querySelector("#subtitle");
  const controlsMiddle = controller.querySelector("#sm-controls-middle");
  const controlBar = controller.querySelector("media-control-bar");
  if (subtitle) subtitle.style.display = "";
  if (controlsMiddle) controlsMiddle.style.display = "";
  if (controlBar) controlBar.style.display = "";
}

// Media URLs carry a JWT in `token` (the worker signs every playlist and chunk URL for 5 hours).
// Its payload is plain base64url JSON, so the page can read the expiry without a secret. Returns
// epoch seconds, or null.
function tokenExpiry(url) {
  try {
    const payload = new URL(url, location.href).searchParams.get("token")?.split(".")[1];
    if (!payload) return null;
    const exp = Number(JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))).exp);
    return Number.isFinite(exp) ? exp : null;
  } catch (_) {
    return null;
  }
}

// A fresh source URL from Gems (POST /player/refresh-token), in exchange for the grant the page
// was rendered with. Gems checks access again; a refusal rejects.
async function fetchFreshSource(endpoint, grant) {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ grant }),
    signal: AbortSignal.timeout(15 * 1000),
  });
  if (!response.ok) throw new Error(`refresh refused: HTTP ${response.status}`);
  const { src } = await response.json();
  if (!src) throw new Error("refresh returned no source");
  return src;
}

export const Video = {
  mounted() {
    // Track current asset identity to detect LiveView patch changes
    this.prevElDataId = this.el.dataset.id;
    // Will be set after attachElements when video is available
    this.prevVideoDataId = null;
    this.prevSrc = null;
    this.isPreview = this.el.dataset.preview === "true";
    this.isPreviewPlayable = this.el.dataset.previewPlayable === "true";
    this.previewStart = this.readPreviewStart();
    this.previewAccessBannerShown = false;
    this.previewLoadStoppedBy403 = false;
    this.lastPreviewForbiddenAt = 0;
    this.previewShouldResumeAfterForbidden = false;
    this.hasMultipleRenditions = this.el.dataset.hasMultipleRenditions === "true";

    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.el.dataset.title,
        artist: this.el.dataset.speaker,
        artwork: [{ src: this.el.dataset.poster }],
      });
    }

    this.pageLoadedAt = Date.now();

    this.attachElements();

    // Track if we purposely started muted to satisfy autoplay policies
    this.startedMutedByAutoplay = false;

    // Chrome-less embeds: no controls, and no way for the viewer to unmute
    this.noPlayerUI = this.el.dataset.noPlayerUi === "true";
    this.forceMuted = this.el.dataset.forceMuted === "true";

    if (this.forceMuted) {
      this.video.muted = true;
      this.video.setAttribute("muted", "");
      this.startedMutedByAutoplay = true;
    }

    if (this.el.dataset.loopPlayback === "true") {
      const loopStart = this.previewStart || 0;

      // Native looping is seamless, but always restarts at 0, which is out of
      // bounds for a preview that starts later in the talk.
      if (loopStart === 0) {
        this.video.loop = true;
        this.video.setAttribute("loop", "");
      } else {
        this.boundRestartPlayback = () => {
          this.video.currentTime = loopStart;
          this.video.play().catch(() => {});
        };
        this.video.addEventListener("ended", this.boundRestartPlayback);
      }
    }

    // don't push a videotime event to the server immediately on page load, because otherwise you get a flash of the "wrong" segment when loading a &item= url
    this.lastCalled = Date.now();

    // workaround mediachrome bug where mute -> reload = not possible to unmute.
    // so just always start unmuted — except when we intentionally muted for autoplay
    setTimeout(() => {
      if (this.video.muted && !this.startedMutedByAutoplay) {
        this.video.muted = false;
      }
    }, 500);

    // on mobile, first show the interface before triggering any taps
    this.el.addEventListener("userinactivechange", () => {
      const isUserActive = !this.el.hasAttribute("userinactive");
      if (isUserActive) {
        setTimeout(() => {
          this.el.querySelector("#blocker").classList.add("hidden");
        }, 250);
      } else if (!this.video.paused) {
        this.el.querySelector("#blocker").classList.remove("hidden");
      }
    });

    this.handleEvent("videotime", ({ time, dispatcher }) => {
      // fixes race condition with rendering
      setTimeout(() => {
        if (
          this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA ||
          this.video.readyState >= HTMLMediaElement.HAVE_METADATA
        ) {
          this.playVideoAtTime(time, dispatcher);
        } else {
          // try until video is loaded
          const interval = setInterval(() => {
            if (
              this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA ||
              this.video.readyState >= HTMLMediaElement.HAVE_METADATA
            ) {
              this.playVideoAtTime(time, dispatcher);
              clearInterval(interval);
            }
          }, 150);
        }
      }, 100);
    });

    // Track desired playback rate to work around Safari resets
    this.desiredPlaybackRate = parseFloat(
      this.el.querySelector("#playback-rate-menu")?.dataset.playbackRate ||
        "1.0",
    );
    this.settingPlaybackRate = false; // internal guard for programmatic sets
    this.ignoreRateChangeUntil = 0; // timestamp (ms) until which we ignore ratechange
    // Apply immediately so initial state matches user preference even before play
    this.applyDesiredPlaybackRate();

    // Coming back to a tab that sat in the background: swap in fresh media tokens if they would
    // expire before the talk ends, usually while the player is still paused.
    this.boundVisibilityRefresh = () => {
      if (document.visibilityState === "visible") this.maybeRefreshSource("visible");
    };
    document.addEventListener("visibilitychange", this.boundVisibilityRefresh);

    this.video.addEventListener("play", (e) => {
      this.maybeRefreshSource("play");
      this.resumePreviewLoadAfterForbidden();
      this.updateMiniPlayerControlsState();

      // Safari sometimes resets playbackRate on play; re-apply shortly
      this.ignoreRateChangeUntil = performance.now() + 500;
      this.applyDesiredPlaybackRate();
    });

    this.video.addEventListener("pause", (e) => {
      this.updateMiniPlayerControlsState();
    });

    this.boundHandlePreviewSeek = this.handlePreviewSeek.bind(this);
    this.video.addEventListener("seeking", this.boundHandlePreviewSeek);
    this.video.addEventListener("seeked", this.boundHandlePreviewSeek);

    this.video.addEventListener("ratechange", (e) => {
      // Ignore programmatic sets or the brief window after play/playing
      if (this.settingPlaybackRate) return;
      if (performance.now() < this.ignoreRateChangeUntil) return;

      const menu = this.el.querySelector("#playback-rate-menu");
      const storedPlaybackRate = parseFloat(
        menu?.dataset.playbackRate || "1.0",
      );

      if (this.video.playbackRate !== storedPlaybackRate) {
        // User-initiated change: persist and remember for Safari workarounds
        this.desiredPlaybackRate = this.video.playbackRate;
        if (menu) menu.dataset.playbackRate = String(this.desiredPlaybackRate);
        this.pushEvent("set_player_setting", {
          key: "playback_rate",
          value: this.video.playbackRate,
        });
      }
    });

    this.handleEvent(
      "update_mini_player_controls_state",
      this.updateMiniPlayerControlsState.bind(this),
    );

    this.handleEvent("play_video", () => {
      // Restore player UI in case it was hidden by a bumper overlay
      showPlayerUI();
      this.video.play();
      // Proactively re-apply desired rate around programmatic plays
      this.ignoreRateChangeUntil = performance.now() + 500;
      this.applyDesiredPlaybackRate();
    });

    this.handleEvent("pause_video", () => {
      this.video.pause();
    });

    this.handleEvent("show_subscription_modal", () => {
      const trigger = document.getElementById("preview-subscription-trigger");
      if (trigger) trigger.click();
    });

    this.handleEvent("highlight_segment", this.highlightSegment.bind(this));

    if (!this.noPlayerUI) {
      this.boundKeyboardShortcuts = this.keyboardShortcuts.bind(this);
      window.addEventListener("keydown", this.boundKeyboardShortcuts);
    }

    // Also re-apply on 'playing' which can occur after decode starts in Safari
    this.video.addEventListener("playing", () => {
      this.ignoreRateChangeUntil = performance.now() + 500;
      this.applyDesiredPlaybackRate();
    });

    // Enable custom rendering of subtitles
    setTimeout(() => {
      this.reloadSubtitles();
      this.renderSubtitles();
      setTimeout(() => {
        this.potentiallyDisableSubtitles();
      }, 1);
    }, 1000);

    let subtitlesVisible;

    // hide subtitles when turned off
    this.video.textTracks.addEventListener("change", (e) => {
      subtitlesVisible = false;

      for (let i = 0; i < e.target.length; i++) {
        const track = e.target[i];
        if (track.kind === "subtitles" && track.mode === "showing") {
          this.cueChange.bind(this, track);
          subtitlesVisible = true;
        }
      }

      if (!subtitlesVisible) {
        this.subtitle.style.opacity = 0;
        this.subtitle.style.transform = "scale(0.99)";
      }
    });

    this.el
      .querySelector("media-seek-forward-button")
      .addEventListener("click", () => {
        this.hideControls();
      });

    this.el
      .querySelector("media-seek-backward-button")
      .addEventListener("click", () => {
        this.hideControls();
      });

    this.el.addEventListener("mediashowsubtitlesrequest", (e) => {
      const valueMatch = e.detail.match(/:([^:]+):/);
      if (valueMatch && valueMatch[1]) {
        this.pushEvent("set_player_setting", {
          key: "subtitle_language",
          value: valueMatch[1],
        });
      } else {
        // If no subtitles are selected ("Off" option), send null
        this.pushEvent("set_player_setting", {
          key: "subtitle_language",
          value: null,
        });
      }
    });

    // unmute video when clicking on video or button
    this.video.addEventListener("click", this.unmuteVideo.bind(this));
    this.el
      .querySelector("#autoplay-muted")
      .addEventListener("click", this.unmuteVideo.bind(this));

    // hide muted button when video is unmuted
    this.video.addEventListener("volumechange", () => {
      if (!this.video.muted) {
        this.el.querySelector("#autoplay-muted").classList.add("hidden");
      }
    });

    if (this.subtitleSizeMenu) {
      this.subtitleSizeMenu.addEventListener("click", (event) => {
        const target = event.target.closest("media-chrome-menu-item");

        if (!target) return;

        const size = target.dataset.subtitleSize;
        if (size) {
          this.pushEvent("set_player_setting", {
            key: "subtitle_size",
            value: size,
          });

          this.changeSubtitleSize(size);
        }
      });
    }

    const subtitlePlacementMenu = this.el.querySelector(
      "#subtitle-placement-menu",
    );

    if (subtitlePlacementMenu) {
      subtitlePlacementMenu.addEventListener("click", (event) => {
        const target = event.target.closest("media-chrome-menu-item");
        if (!target) return;

        const placement = target.dataset.subtitlePlacement;
        if (placement) {
          this.pushEvent("set_player_setting", {
            key: "subtitle_placement",
            value: placement,
          });
          this.changeSubtitlePlacement(placement);
        }
      });
    }

    const autoplayMenu = this.el.querySelector("#autoplay-menu");

    if (autoplayMenu) {
      autoplayMenu.addEventListener("click", (event) => {
        const target = event.target.closest("media-chrome-menu-item");
        if (!target) return;

        const autoplayValue = target.dataset.autoplay;
        if (autoplayValue) {
          this.pushEvent("set_player_setting", {
            key: "autoplay",
            value: autoplayValue === "true",
          });
        }
      });
    }

    setTimeout(() => {
      // make sure it's not called on page load
      this.video.addEventListener(
        "timeupdate",
        this.throttledTimeUpdate.bind(this),
      );
    }, 1000);

    // Pre-compute autoplay intent for initial load and hint the browser
    this.shouldAutoplayDecision = this.computeShouldAutoplay();
    if (this.shouldAutoplayDecision) {
      // Adding the attribute early can improve Safari autoplay behavior
      this.video.setAttribute("autoplay", "");
    }

    // Try to autoplay once metadata is available (most reliable time)
    this.boundHandleInitialAutoplay = this.handleInitialAutoplay.bind(this);
    this.video.addEventListener(
      "loadedmetadata",
      this.boundHandleInitialAutoplay,
      {
        once: true,
      },
    );
    this.video.addEventListener("canplay", this.boundHandleInitialAutoplay, {
      once: true,
    });
    // Fallback: in case metadata already loaded, attempt shortly after mount
    setTimeout(this.boundHandleInitialAutoplay, 200);
  },

  computeShouldAutoplay() {
    // Don't autoplay when the tab is not visible
    if (document.visibilityState !== "visible") {
      return false;
    }

    // Don't autoplay when the free bumper overlay is visible
    const freeBumper = document.getElementById("bumper-free");
    if (freeBumper && !freeBumper.classList.contains("hidden")) {
      return false;
    }

    const dataVal = this.el.dataset.autoplay;
    if (dataVal === "true") return true;
    if (dataVal === "false") return false;
    return false;
  },

  changeSubtitlePlacement(placement) {
    if (placement === "top") {
      this.subtitle.style.top = "1.5rem";
      this.subtitle.style.bottom = "auto";
    } else {
      this.subtitle.style.bottom = "0";
      this.subtitle.style.top = "auto";
    }
  },

  changeSubtitleSize(size) {
    // Remove existing subtitle size classes
    this.subtitleInner.classList.remove(
      "subtitle-size-small",
      "subtitle-size-medium",
      "subtitle-size-large",
      "subtitle-size-huge",
    );

    // Add the new subtitle size class
    this.subtitleInner.classList.add(`subtitle-size-${size}`);
  },

  updateMiniPlayerControlsState() {
    const miniPlayerControlsPlay = document.querySelector(
      "#mini-player-controls-play",
    );
    const miniPlayerControlsPause = document.querySelector(
      "#mini-player-controls-pause",
    );
    const miniPlayerControlsBack = document.querySelector(
      "#mini-player-controls-back",
    );
    const miniPlayerControlsForward = document.querySelector(
      "#mini-player-controls-forward",
    );

    if (miniPlayerControlsPlay) {
      if (this.video.paused) {
        miniPlayerControlsPlay.classList.remove("hidden");
        miniPlayerControlsPlay.classList.add("inline-flex");
        miniPlayerControlsPause.classList.add("hidden");
        miniPlayerControlsPause.classList.remove("inline-flex");
        miniPlayerControlsBack.classList.add(
          "opacity-50",
          "pointer-events-none",
        );
        miniPlayerControlsForward.classList.add(
          "opacity-50",
          "pointer-events-none",
        );
      } else {
        miniPlayerControlsPlay.classList.add("hidden");
        miniPlayerControlsPlay.classList.remove("inline-flex");
        miniPlayerControlsPause.classList.remove("hidden");
        miniPlayerControlsPause.classList.add("inline-flex");
        miniPlayerControlsBack.classList.remove(
          "opacity-50",
          "pointer-events-none",
        );
        miniPlayerControlsForward.classList.remove(
          "opacity-50",
          "pointer-events-none",
        );
      }
    }
  },

  playVideoAtTime(time, dispatched) {
    if (this.skipServerVideoTime) { //because the video keeps playing after the disconnect, now the server ("resume where you left off") has slightly stale time, compared to what we have. So ignore the server's time and use our own.
      this.skipServerVideoTime = false;
      return;
    }

    this.video.currentTime = time;
    if (dispatched && this.video.paused) {
      this.video.play();
    }
  },

  throttledTimeUpdate() {
    const now = Date.now();
    if (now - this.lastCalled >= 1000) {
      this.lastCalled = now;
      this.pushEvent("videotime", {
        time: this.video.currentTime,
        id: this.el.dataset.id,
      });
    }
  },

  // Decide whether to autoplay and attempt with safe fallbacks
  async handleInitialAutoplay() {
    if (this.autoplayAttempted) return;
    this.autoplayAttempted = true;

    // Consolidated decision
    let shouldAutoplay = this.shouldAutoplayDecision;

    if (!shouldAutoplay || canAutoplay == false) {
      try {
        this.video.pause();
      } catch (_) {}
      this.video.removeAttribute("autoplay");

      if (!this.forceMuted) {
        this.video.removeAttribute("muted");
        this.video.muted = false;
        this.startedMutedByAutoplay = false;
      }

      this.hideMutedBadge();
      // Help media-chrome reflect paused state immediately
      this.el
        .querySelectorAll("media-play-button")
        .forEach((button) => button.setAttribute("mediapaused", ""));
      this.updateMiniPlayerControlsState();
      return;
    }

    // Ensure we have something buffered enough to start
    const attemptPlay = async () => {
      try {
        // Hint some platforms by setting the attribute as well
        this.video.setAttribute("autoplay", "");
        await this.video.play();
        return true;
      } catch (e) {
        return false;
      }
    };

    // First try with current mute state
    let ok = await attemptPlay();
    if (ok) {
      if (this.forceMuted) this.showMutedBadge();
      return;
    }

    // If blocked, try muted autoplay and show unmute hint
    this.video.muted = true;
    this.video.setAttribute("muted", "");
    this.startedMutedByAutoplay = true;
    this.showMutedBadge();

    ok = await attemptPlay();
    if (ok) return;

    // Final small retry after a short delay in case buffering/attach was late
    setTimeout(() => {
      attemptPlay();
    }, 300);
  },

  attachElements() {
    this.video = this.el.querySelector("#video");
    this.subtitle = this.el.querySelector("#subtitle");
    this.subtitleInner = this.el.querySelector("#subtitle-inner");
    this.nativeSubtitleStyle = document.createElement("style");
    this.subtitleSizeMenu = this.el.querySelector("#subtitle-size-menu");
    this.subtitlePlacementMenu = this.el.querySelector(
      "#subtitle-placement-menu",
    );

    this.el.appendChild(this.nativeSubtitleStyle);

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      const pipButton = this.el.querySelector("media-pip-button");
      if (pipButton) {
        pipButton.style.setProperty("display", "none", "important");
      }
    }

    if (this.subtitleSizeMenu) {
      const selectedItem = this.subtitleSizeMenu.querySelector(
        "media-chrome-menu-item[checked]",
      );

      if (selectedItem) {
        this.changeSubtitleSize(selectedItem.dataset.subtitleSize);
      }
    }

    if (this.subtitlePlacementMenu) {
      const selectedItem = this.subtitlePlacementMenu.querySelector(
        "media-chrome-menu-item[checked]",
      );

      if (selectedItem) {
        this.changeSubtitlePlacement(selectedItem.dataset.subtitlePlacement);
      }
    }

    if (this.el.querySelector("#playback-rate-menu")) {
      this.video.addEventListener("loadedmetadata", () => {
        // Initialize desired rate and set both default and current
        const playbackRate =
          this.el.querySelector("#playback-rate-menu").dataset.playbackRate ||
          "1.0";
        this.desiredPlaybackRate = parseFloat(playbackRate);
        this.applyDesiredPlaybackRate();
      });
    }

    // The element carries no src attribute: nothing plays natively (the chunks need
    // the player signature), and a native src made WebKit start loading the playlist
    // before hls.js attached and fire a spurious error on the element.
    const src = this.video.getAttribute("data-original-src");

    if (this.src != src) {
      this.src = src;

      if (Hls.isSupported()) {
        this.teardownRenditionBridge();
        this.hls = new Hls(this.getHlsConfig());
        this.hls.loadSource(this.src);
        this.hls.attachMedia(this.video);
        // a new video: start counting and reporting afresh
        this.resetPlaybackReporting();
        // before the preview handler, which pauses the video on a 403
        this.attachPlaybackReporting();
        this.attachPreviewHlsErrorHandler();
        this.removeMismatchedFrameRateLevels();
        this.capAutoLevelTo1080p();
        this.setupRenditionBridge();
      } else {
        alert("Your browser or device is too old to view talks on Living Gems. Please update Safari to version 17.1 or later.");
        return;
      }

      this.seekToPreviewStartWhenReady();

      for (let i = 1; i <= 5; i++) {
        setTimeout(this.forceChapters.bind(this), i * 1000);
      }
    } else {
      this.el
        .querySelectorAll("media-play-button")
        .forEach((button) => button.setAttribute("mediapaused", true));

      setTimeout(() => {
        this.el.removeAttribute("medialoading");
        this.el
          .querySelector("media-loading-indicator")
          .removeAttribute("medialoading");
      }, 100);

      for (let i = 1; i <= 5; i++) {
        setTimeout(this.forceChapters.bind(this), i * 1000);
      }
    }
  },

  // Some videos carry a 1080p rendition at double the frame rate of the others
  // (50 or 59.94 fps against 25 or 29.97). The encoder cut those segments
  // mid-GOP: each one drops a few frames at the boundary and its first B-frames
  // reference pictures from the previous segment. Chrome conceals that, WebKit
  // throws a decode error and the picture goes black. Until those videos are
  // re-encoded, drop such levels from the ladder altogether, which keeps them
  // out of both auto selection and the quality menu. Ladders where every
  // rendition shares one frame rate are left alone.
  removeMismatchedFrameRateLevels() {
    const hls = this.hls;
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      const rates = hls.levels.map((level) => level.frameRate || 0).filter((rate) => rate > 0);
      if (rates.length < 2) return;
      const baseRate = Math.min(...rates);
      // highest index first, so earlier indexes stay valid while removing
      for (let index = hls.levels.length - 1; index >= 0; index--) {
        const rate = hls.levels[index].frameRate || 0;
        if (rate > baseRate * 1.5 && hls.levels.length > 1) {
          hls.removeLevel(index);
        }
      }
    });
  },

  // Keep "auto" from picking anything above 1080p (e.g. 4K); manual selection is unaffected.
  capAutoLevelTo1080p() {
    const hls = this.hls;
    const applyCap = () => {
      let cap = -1;
      hls.levels.forEach((level, index) => {
        if (Math.min(level.width || 0, level.height || 0) <= 1080) cap = index;
      });
      hls.autoLevelCapping = cap === hls.levels.length - 1 ? -1 : cap;
    };
    hls.on(Hls.Events.MANIFEST_PARSED, applyCap);
    hls.on(Hls.Events.LEVELS_UPDATED, applyCap);
  },

  setupRenditionBridge() {
    if (!this.hasMultipleRenditions || !this.video || !this.hls) return;

    this.teardownRenditionBridge();

    const rebuildRenditions = () => {
      const levels = this.hls?.levels || [];
      if (!levels.length) return;

      const renditions = levels.map((level, index) => ({
        id: `hls-level-${index}`,
        width: level.width,
        height: level.height,
        bitrate: level.bitrate,
      }));

      const renditionEvents = new EventTarget();
      const videoRenditions = renditions.slice();
      videoRenditions.addEventListener =
        renditionEvents.addEventListener.bind(renditionEvents);
      videoRenditions.removeEventListener =
        renditionEvents.removeEventListener.bind(renditionEvents);
      videoRenditions.dispatchEvent =
        renditionEvents.dispatchEvent.bind(renditionEvents);

      Object.defineProperty(videoRenditions, "selectedIndex", {
        configurable: true,
        enumerable: true,
        get: () => {
          if (!this.hls) return -1;
          if (typeof this.hls.manualLevel === "number") return this.hls.manualLevel;
          return this.hls.currentLevel;
        },
        set: (value) => {
          if (!this.hls) return;
          const nextLevel = Number.isFinite(Number(value)) ? Number(value) : -1;
          this.hls.currentLevel = nextLevel;
          videoRenditions.dispatchEvent(new Event("change"));
        },
      });

      this.video.videoRenditions = videoRenditions;

      for (const _ of videoRenditions) {
        videoRenditions.dispatchEvent(new Event("addrendition"));
      }

      videoRenditions.dispatchEvent(new Event("change"));
    };

    const onLevelChange = () => {
      this.video?.videoRenditions?.dispatchEvent?.(new Event("change"));
    };

    this.boundHlsLevelsUpdated = rebuildRenditions;
    this.boundHlsManifestParsed = rebuildRenditions;
    this.boundHlsLevelSwitched = onLevelChange;

    this.hls.on(Hls.Events.LEVELS_UPDATED, this.boundHlsLevelsUpdated);
    this.hls.on(Hls.Events.MANIFEST_PARSED, this.boundHlsManifestParsed);
    this.hls.on(Hls.Events.LEVEL_SWITCHED, this.boundHlsLevelSwitched);

    rebuildRenditions();
  },

  teardownRenditionBridge() {
    if (this.hls && this.boundHlsLevelsUpdated) {
      this.hls.off(Hls.Events.LEVELS_UPDATED, this.boundHlsLevelsUpdated);
      this.boundHlsLevelsUpdated = null;
    }

    if (this.hls && this.boundHlsManifestParsed) {
      this.hls.off(Hls.Events.MANIFEST_PARSED, this.boundHlsManifestParsed);
      this.boundHlsManifestParsed = null;
    }

    if (this.hls && this.boundHlsLevelSwitched) {
      this.hls.off(Hls.Events.LEVEL_SWITCHED, this.boundHlsLevelSwitched);
      this.boundHlsLevelSwitched = null;
    }

    if (this.video?.videoRenditions) {
      for (const _ of this.video.videoRenditions) {
        this.video.videoRenditions.dispatchEvent(new Event("removerendition"));
      }
      delete this.video.videoRenditions;
    }
  },

  // Safely apply desired rate without triggering persistence logic
  applyDesiredPlaybackRate() {
    try {
      this.settingPlaybackRate = true;
      if (
        typeof this.desiredPlaybackRate === "number" &&
        !Number.isNaN(this.desiredPlaybackRate)
      ) {
        this.video.defaultPlaybackRate = this.desiredPlaybackRate;
        this.video.playbackRate = this.desiredPlaybackRate;
      }
    } finally {
      // Defer clearing the flag to allow the ratechange event to fire first
      setTimeout(() => (this.settingPlaybackRate = false), 0);
    }
  },

  // Reports to PostHog an event when a video starts playing and an exception
  // when playback fails, so failures can be compared per browser as a share
  // of plays. Each failure is reported once per loaded video.
  resetPlaybackReporting() {
    this.playbackStartedAt = null;
    this.reportedPlaybackErrors = new Set();
    this.mediaRecovery = { attempts: 0, lastAt: 0, position: null, pending: null };
  },

  // Also called after a reconnect, which replaces the Hls instance; the
  // counters survive that because it is still the same video.
  attachPlaybackReporting() {
    if (this.hls && !this.hls.playbackReportingAttached) {
      this.hls.playbackReportingAttached = true;

      this.hls.on(Hls.Events.ERROR, (_event, data) => {
        const status = hlsResponseStatus(data);
        const forbidden = status === 401 || status === 403;

        // A tab left open outlives its media tokens: a 403 on an expired one is stale tokens,
        // not lost access, and gets a fresh source (see refreshSource).
        if (this.refreshingExpiredToken(data)) return;

        // A chunk 403 on a playable preview means the preview window ended,
        // which attachPreviewHlsErrorHandler turns into the subscription prompt.
        if (
          forbidden &&
          this.isPreview &&
          this.isPreviewPlayable &&
          isChunkRequestError(data)
        ) {
          return;
        }
        // hls.js retries other non-fatal errors itself
        if (!data?.fatal && !forbidden) return;

        const code = `${data?.details || "unknown"}${status ? ` ${status}` : ""}`;
        const properties = {
          hls_error_type: data?.type ?? null,
          hls_error_details: data?.details ?? null,
          hls_error_fatal: Boolean(data?.fatal),
          http_status: status,
        };
        // A fatal media error (failed append, codec switch, decoder reset) is
        // usually recoverable by resetting the MediaSource; hls.js leaves that
        // to the application. Codec incompatibility is not: nothing can play.
        if (
          data?.fatal &&
          data?.type === Hls.ErrorTypes.MEDIA_ERROR &&
          !/IncompatibleCodecs/.test(data?.details || "")
        ) {
          this.handleMediaError(code, properties);
          return;
        }

        const detail = this.reportPlaybackError(code, properties);
        if (detail) this.stopPlaybackWithError(detail);
      });
    }

    if (this.video && !this.video.playbackReportingAttached) {
      this.video.playbackReportingAttached = true;

      this.video.addEventListener("playing", () => {
        const pending = this.mediaRecovery?.pending;
        if (pending) {
          this.mediaRecovery.pending = null;
          playbackAnalytics()?.capture("video_playback_recovered", {
            ...this.playbackProperties(),
            ...pending,
          });
        }
        if (this.playbackStartedAt) return;
        this.playbackStartedAt = Date.now();
        // Only the embed sends this: it has no pageviews to compare failures against, the app does.
        const analytics = playbackAnalytics();
        if (analytics?.get_property("embed")) {
          analytics.capture("video_playback_started", this.playbackProperties());
        }
      });

      this.video.addEventListener("error", () => {
        const error = this.video.error;
        // No MediaError means nothing failed to play: WebKit fires error on an
        // element whose load was reset, for example when hls.js swaps the source.
        if (!error) return;
        const code = `MediaError ${error.code}`;
        const properties = {
          media_error_code: error.code,
          media_error_message: error.message || null,
        };
        // MEDIA_ERR_DECODE: the decoder choked on a segment. WebKit does this on
        // segments Chrome plays through, and hls.js only logs it, so recover here.
        if (error.code === MediaError.MEDIA_ERR_DECODE && this.hls) {
          this.handleMediaError(code, properties);
          return;
        }
        const detail = this.reportPlaybackError(code, properties);
        if (detail) this.stopPlaybackWithError(detail);
      });
    }
  },

  // Decode and buffer errors are often one bad segment, not a dead stream. Try,
  // in order: reset the MediaSource (hls.js resumes from the current position);
  // reset again with the audio codec swapped, hls.js's own second step; move
  // auto quality one level down and step past the bad spot. Only when all of
  // that fails within the same stretch of video does the viewer get the error
  // box. Each attempt is reported so PostHog shows how often recovery works.
  handleMediaError(code, properties) {
    const video = this.video;
    const hls = this.hls;
    const now = Date.now();
    const position = video?.currentTime ?? 0;
    const duration = video?.duration;
    const recovery = this.mediaRecovery || (this.mediaRecovery = { attempts: 0, lastAt: 0, position: null, pending: null });

    // The native error and hls.js's own report of the same failure arrive together.
    if (now - recovery.lastAt < 1500) return;

    // A decoder error on the last frames is the end of the video, not a failure worth a box.
    if (Number.isFinite(duration) && duration - position < 2) {
      playbackAnalytics()?.capture("video_playback_media_error_at_end", {
        ...this.playbackProperties(),
        ...properties,
        trigger: code,
      });
      return;
    }

    // Trouble more than 30 seconds past the previous one counts as a fresh spot.
    if (recovery.position !== null && Math.abs(position - recovery.position) > 30) {
      recovery.attempts = 0;
    }
    recovery.lastAt = now;
    recovery.position = position;

    const steps = ["reset_media_source", "swap_audio_codec", "step_down_level"];
    const step = steps[recovery.attempts];
    if (!step || !hls) {
      const detail = this.reportPlaybackError(code, {
        ...properties,
        recovery_attempts: recovery.attempts,
      });
      if (detail) this.stopPlaybackWithError(detail);
      return;
    }
    recovery.attempts += 1;

    const wasPlaying = !video.paused && !video.ended;
    const attempt = { recovery_step: step, recovery_attempt: recovery.attempts, trigger: code };
    recovery.pending = attempt;
    playbackAnalytics()?.capture("video_playback_recovery", {
      ...this.playbackProperties(),
      ...properties,
      ...attempt,
    });

    try {
      if (step === "swap_audio_codec") {
        hls.swapAudioCodec();
      } else if (step === "step_down_level") {
        const level = hls.currentLevel >= 0 ? hls.currentLevel : hls.loadLevel;
        if (level > 0) {
          hls.autoLevelCapping = level - 1;
          hls.nextLoadLevel = level - 1;
        }
        // skip the frames the decoder refused rather than hitting them again
        video.currentTime = position + 0.5;
      }
      // listen before the reset: the reattach fires MEDIA_ATTACHED once the
      // new MediaSource opens, and playback should resume where it stopped
      if (wasPlaying) {
        hls.once(Hls.Events.MEDIA_ATTACHED, () => {
          video.play()?.catch?.(() => {});
        });
      }
      hls.recoverMediaError();
    } catch (_) {
      const detail = this.reportPlaybackError(code, {
        ...properties,
        recovery_attempts: recovery.attempts,
      });
      if (detail) this.stopPlaybackWithError(detail);
    }
  },

  // Returns a short description for the viewer's error message, or null when
  // this error was already reported for the current video.
  reportPlaybackError(code, properties) {
    if (this.reportedPlaybackErrors?.has(code)) return null;
    this.reportedPlaybackErrors?.add(code);

    const props = { ...this.playbackProperties(), ...properties };
    playbackAnalytics()?.captureException(new Error(`Video playback failed: ${code}`), props);

    // Shown to the viewer, so a screenshot tells us what failed, where in the
    // video, which media pipeline, and which build.
    return [
      code,
      formatClock(this.video?.currentTime),
      props.media_source,
      typeof appRevision === "string" && appRevision ? appRevision.slice(0, 7) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  },

  playbackProperties() {
    const level = this.hls?.levels?.[this.hls.currentLevel];

    return {
      video_id: this.el.dataset.id,
      preview: this.isPreview,
      position_seconds: roundedSeconds(this.video?.currentTime),
      duration_seconds: roundedSeconds(this.video?.duration),
      seconds_since_playback_started: this.playbackStartedAt
        ? Math.round((Date.now() - this.playbackStartedAt) / 1000)
        : null,
      quality_height: level?.height ?? null,
      quality_auto: this.hls ? this.hls.autoLevelEnabled : null,
      // Same rule as hls.js getMediaSource(): with preferManagedMediaSource off
      // (the default since 1.6.19) ManagedMediaSource is only used where there is
      // no MediaSource, which is iPhone. Mac Safari and iPad use MediaSource.
      media_source: window.MediaSource ? "mse" : window.ManagedMediaSource ? "mms" : "none",
    };
  },

  attachPreviewHlsErrorHandler() {
    if (!this.hls) return;
    if (this.hls.previewErrorHandlerAttached) return;
    this.hls.previewErrorHandlerAttached = true;

    this.hls.on(Hls.Events.ERROR, (_event, data) => {
      const responseCode = hlsResponseStatus(data);
      const isForbidden = responseCode === 401 || responseCode === 403;

      if (!isForbidden || !isChunkRequestError(data)) return;

      // an expired token is swapped for a fresh one in attachPlaybackReporting
      if (this.refreshingExpiredToken(data)) return;

      // Outside a playable preview a 403 is a failure, handled in attachPlaybackReporting.
      if (this.isPreview && this.isPreviewPlayable) {
        this.handlePreviewPlaybackForbidden();
      }
    });
  },

  // Epoch seconds when the media tokens hls.js uses next expire: the chunks and playlist of the
  // rendition being played (the worker signs them for 5 hours when it serves the playlists), or
  // the page's source before hls.js has loaded anything. Null when there is no token.
  mediaTokensExpireAt() {
    const hls = this.hls;
    const level = hls?.levels?.[hls.loadLevel >= 0 ? hls.loadLevel : hls.currentLevel];
    const urls = [
      level?.details?.fragments?.[0]?.url,
      level?.uri,
      level?.details ? null : this.video?.getAttribute("data-original-src"),
    ];
    const expiries = urls.map((url) => url && tokenExpiry(url)).filter((exp) => exp);
    return expiries.length ? Math.min(...expiries) : null;
  },

  // Whether an hls.js error is a 403 on an expired token that a fresh source is (being) fetched
  // for, so the error handlers leave it alone.
  refreshingExpiredToken(data) {
    if (hlsResponseStatus(data) !== 403) return false;
    const exp = tokenExpiry(hlsErrorUrl(data));
    if (exp === null || exp > Date.now() / 1000) return false;
    return this.sourceRefreshInFlight || this.refreshSource("forbidden");
  },

  // Swaps in fresh media tokens when the current ones would expire before the rest of the talk has
  // played (up to 4 hours of it, fresh tokens last 5). A swap costs the buffer and a moment of
  // loading, so it is done when the viewer comes back to the tab or presses play, not mid-way.
  maybeRefreshSource(trigger) {
    const exp = this.mediaTokensExpireAt();
    if (exp === null || !this.video) return;

    const rate = this.video.playbackRate || 1;
    const duration = Number.isFinite(this.video.duration) ? this.video.duration : 0;
    const remaining = Math.max(0, duration - this.video.currentTime) / rate;
    if (exp - Date.now() / 1000 > Math.min(remaining, 4 * 60 * 60) + 60) return;

    this.refreshSource(trigger);
  },

  // Fetches a fresh source URL from the server (which checks access again) and swaps the hls.js
  // instance, keeping position, play state and a chosen quality. Returns false when it is not
  // tried: no grant on the page, a refresh under way, or backing off. After a swap, the next one
  // waits 5 minutes: a 403 that soon is not about stale tokens and is left to the error handling.
  // A failed refresh waits 1 minute, doubling each time, up to 30.
  refreshSource(trigger) {
    const grant = this.el.dataset.sourceRefreshGrant;
    const endpoint = this.el.dataset.sourceRefreshUrl;
    if (!grant || !endpoint || !Hls.isSupported()) return false;
    if (this.sourceRefreshInFlight || Date.now() < (this.sourceRefreshNotBefore || 0)) return false;

    // The player this refresh belongs to: a reconnect, a new talk or teardown replaces it, and a
    // result arriving after that must not touch the new one.
    const player = this.hls;
    const video = this.video;
    const stale = () => this.hls !== player || this.video !== video;

    // Negative once the tokens have expired: a refresh on play or on coming back is ahead of
    // expiry, one after a 403 is behind it.
    const expiresAt = this.mediaTokensExpireAt();
    const refreshProperties = () => ({
      ...this.playbackProperties(),
      trigger,
      token_expires_in_seconds:
        expiresAt === null ? null : Math.round(expiresAt - Date.now() / 1000),
      stale: stale(),
    });

    this.sourceRefreshInFlight = true;
    fetchFreshSource(endpoint, grant)
      .then((src) => {
        this.sourceRefreshFailures = 0;
        this.sourceRefreshNotBefore = Date.now() + 5 * 60 * 1000;
        playbackAnalytics()?.capture("video_playback_source_refreshed", refreshProperties());
        if (!stale()) this.swapSource(src);
      })
      .catch((error) => {
        this.sourceRefreshFailures = (this.sourceRefreshFailures || 0) + 1;
        this.sourceRefreshNotBefore =
          Date.now() + Math.min(60 * 1000 * 2 ** (this.sourceRefreshFailures - 1), 30 * 60 * 1000);
        console.warn("Could not refresh the video source", error);
        playbackAnalytics()?.capture("video_playback_source_refresh_failed", {
          ...refreshProperties(),
          error: String(error?.message || error),
          failures_in_a_row: this.sourceRefreshFailures,
        });
        if (stale() || trigger !== "forbidden") return;
        if (this.isPreview && this.isPreviewPlayable) {
          this.handlePreviewPlaybackForbidden();
          return;
        }
        const detail = this.reportPlaybackError("source refresh failed", { trigger });
        if (detail) this.stopPlaybackWithError(detail);
      })
      .finally(() => {
        this.sourceRefreshInFlight = false;
      });
    return true;
  },

  swapSource(src) {
    const video = this.video;
    // Taken now rather than when the refresh started: buffered video may have played meanwhile.
    const resumeTime = video.currentTime;
    const resumePlaying = !video.paused && !video.ended;
    const old = this.hls;
    const manualLevel = old && !old.autoLevelEnabled ? old.currentLevel : -1;

    // this.src too, or the next LiveView update would see a new source and load it once more
    video.setAttribute("data-original-src", src);
    this.src = src;
    this.teardownRenditionBridge();
    old?.destroy();

    this.hls = new Hls({
      ...this.getHlsConfig(),
      ...(resumeTime > 0 ? { startPosition: resumeTime } : {}),
    });
    this.hls.loadSource(src);
    this.hls.attachMedia(video);
    this.attachPlaybackReporting();
    this.attachPreviewHlsErrorHandler();
    this.removeMismatchedFrameRateLevels();
    this.capAutoLevelTo1080p();
    this.setupRenditionBridge();
    this.hls.once(Hls.Events.MANIFEST_PARSED, () => {
      if (manualLevel >= 0) this.hls.currentLevel = manualLevel;
      if (resumePlaying) video.play()?.catch?.(() => {});
    });
  },

  // Playback cannot continue (expired token, fatal player error, media error):
  // stop and tell the viewer. The event page shows a flash; the embed shows
  // its own box and reads `detail` from the payload.
  stopPlaybackWithError(detail) {
    if (this.hls) {
      this.hls.stopLoad();
    }
    try {
      this.video.pause();
    } catch (_) {}
    // the message sits outside the fullscreen element, so it would otherwise stay hidden
    exitAnyFullscreen(this.video);
    this.clearPlayerLoadingState();
    this.el
      .querySelectorAll("media-play-button")
      .forEach((button) => button.setAttribute("mediapaused", ""));

    this.pushEvent("set_flash", {
      message: `Something went wrong while playing the video. Try refreshing the page, or leave this talk and come back to it. (${detail})`,
      type: "error",
      detail,
    });
  },

  readPreviewStart() {
    const parsed = Number(this.el.dataset.previewStart);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },

  getHlsConfig() {
    if (
      this.isPreview &&
      this.isPreviewPlayable &&
      Number.isFinite(this.previewStart) &&
      this.previewStart > 0
    ) {
      return {
        maxBufferLength: 30,
        maxMaxBufferLength: 45, //see gems-worker/worker.js, this needs to be less than BUFFER_SECONDS, there, otherwise can get 403 error from player buffering which prematurely results in "login to watch". Somehow, m4s chunks behave differently from old .ts ones.
        startPosition: this.previewStart,
        startLevel: -1,
        fLoader: playerSignatureFragLoader,
        //testBandwidth: true  // possibly not needed? https://github.com/video-dev/hls.js/discussions/7443#discussioncomment-14013609. also not sure it works with the preview gating
      };
    }

    return {
      startLevel: -1,
      fLoader: playerSignatureFragLoader,
      //testBandwidth: true // possibly not needed? https://github.com/video-dev/hls.js/discussions/7443#discussioncomment-14013609
    };
  },

  seekToPreviewStartWhenReady() {
    if (
      !this.isPreview ||
      !this.isPreviewPlayable ||
      !Number.isFinite(this.previewStart) ||
      this.previewStart <= 0
    ) {
      return;
    }

    const seekToPreviewStart = () => {
      if (this.video.currentTime < this.previewStart - 0.25) {
        this.video.currentTime = this.previewStart;
      }
    };

    if (this.video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekToPreviewStart();
    }

    this.video.addEventListener("loadedmetadata", seekToPreviewStart, {
      once: true,
    });
  },

  handlePreviewPlaybackForbidden() {
    const now = Date.now();
    if (now - this.lastPreviewForbiddenAt < 500) return;
    this.lastPreviewForbiddenAt = now;

    if (this.isPreviewBannerVisible()) return;

    this.previewAccessBannerShown = true;
    this.previewLoadStoppedBy403 = true;
    this.previewShouldResumeAfterForbidden = !this.video.paused;

    if (this.hls) {
      this.hls.stopLoad();
    }

    try {
      this.video.pause();
    } catch (_) {}

    this.clearPlayerLoadingState();

    this.el
      .querySelectorAll("media-play-button")
      .forEach((button) => button.setAttribute("mediapaused", ""));

    const trigger = document.getElementById("preview-subscription-trigger");
    if (trigger) trigger.click();
  },

  handlePreviewSeek() {
    this.resumePreviewLoadAfterForbidden();
    this.clearPlayerLoadingState();
  },

  resumePreviewLoadAfterForbidden() {
    if (!this.previewLoadStoppedBy403 || !this.hls) return;

    const startPosition = Number.isFinite(this.video?.currentTime)
      ? this.video.currentTime
      : -1;

    try {
      this.hls.startLoad(startPosition);
    } catch (_) {}

    this.previewLoadStoppedBy403 = false;
    this.previewAccessBannerShown = false;

    if (this.previewShouldResumeAfterForbidden && this.video?.paused) {
      this.video.play().catch(() => {});
    }

    this.previewShouldResumeAfterForbidden = false;
  },

  clearPlayerLoadingState() {
    this.el.removeAttribute("medialoading");
    this.el.querySelector("media-loading-indicator")?.removeAttribute("medialoading");
  },

  isPreviewBannerVisible() {
    const banner = document.getElementById("subscription_modal");
    if (!banner) return false;

    const styles = window.getComputedStyle(banner);
    return styles.display !== "none" && styles.visibility !== "hidden";
  },

  updated() {
    this.isPreview = this.el.dataset.preview === "true";
    this.isPreviewPlayable = this.el.dataset.previewPlayable === "true";
    this.previewStart = this.readPreviewStart();

    if (
      this.el.querySelector("media-time-range").mediaChaptersCues &&
      this.el.querySelector("media-time-range").mediaChaptersCues.length > 0
    ) {
      this.chapters =
        this.el.querySelector("media-time-range").mediaChaptersCues;
    }
    this.attachElements();

    // Detect if the underlying media changed due to LiveView patch
    const newElDataId = this.el.dataset.id;
    const newVideoDataId = this.video?.dataset?.id;
    const newSrc = this.video?.getAttribute("data-original-src");
    const mediaChanged =
      (this.prevElDataId && newElDataId && this.prevElDataId !== newElDataId) ||
      (this.prevVideoDataId &&
        newVideoDataId &&
        this.prevVideoDataId !== newVideoDataId) ||
      (this.prevSrc && newSrc && this.prevSrc !== newSrc);

    // Update trackers for next update cycle
    this.prevElDataId = newElDataId || this.prevElDataId;
    this.prevVideoDataId = newVideoDataId || this.prevVideoDataId;
    this.prevSrc = newSrc || this.prevSrc;

    if (mediaChanged) {
      window.scrollTo({ top: 0 });
      this.previewAccessBannerShown = false;
      this.previewLoadStoppedBy403 = false;
      this.previewShouldResumeAfterForbidden = false;

      // Update Media Session metadata for the new item
      if ("mediaSession" in navigator) {
        try {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: this.el.dataset.title,
            artist: this.el.dataset.speaker,
            artwork: [{ src: this.el.dataset.poster }],
          });
        } catch (_) {}
      }

      this.lastCalled = Date.now();

      // Reset autoplay state and recompute intention for the new media
      this.autoplayAttempted = false;
      this.startedMutedByAutoplay = false;
      this.hideMutedBadge();
      this.shouldAutoplayDecision = this.computeShouldAutoplay();

      if (this.shouldAutoplayDecision) {
        this.video.setAttribute("autoplay", "");
      } else {
        this.video.removeAttribute("autoplay");
      }

      // Re-attach a one-time autoplay attempt for the new video element
      this.boundHandleInitialAutoplay =
        this.boundHandleInitialAutoplay ||
        this.handleInitialAutoplay.bind(this);
      this.video.addEventListener(
        "loadedmetadata",
        this.boundHandleInitialAutoplay,
        { once: true },
      );
      this.video.addEventListener("canplay", this.boundHandleInitialAutoplay, {
        once: true,
      });
      // Fallback small delay in case metadata already loaded
      setTimeout(() => this.boundHandleInitialAutoplay(), 150);
    }

    setTimeout(() => {
      this.reloadSubtitles();
      this.renderSubtitles();
      setTimeout(() => {
        this.potentiallyDisableSubtitles();
      }, 1);
    }, 250);

    // LiveView patches can re-apply the original `hidden` class.
    // If we intentionally started muted for autoplay, re-show the badge.
    if (this.startedMutedByAutoplay && this.video && this.video.muted) {
      this.showMutedBadge();
    }
  },

  renderSubtitles() {
    this.nativeSubtitleStyle.innerHTML = `video::cue { opacity: 0; }`;

    // Show native subtitles on fullscreen when on iPhone, because the custom subtitles don't work in that case
    this.video.addEventListener("webkitbeginfullscreen", (event) => {
      this.nativeSubtitleStyle.innerHTML = `video::cue { opacity: 1; }`;
    });

    this.video.addEventListener("webkitendfullscreen", (event) => {
      this.nativeSubtitleStyle.innerHTML = `video::cue { opacity: 0; }`;
    });

    // Get all tracks of kind "subtitles"
    var subtitleTracks = this.video.querySelectorAll('track[kind="subtitles"]');

    // Loop through all subtitle tracks and attach the event listener
    subtitleTracks.forEach((track) => {
      var textTrack = track.track;
      track.addEventListener(
        "cuechange",
        this.cueChange.bind(this, textTrack),
        false,
      );
    });
  },

  potentiallyDisableSubtitles() {
    // The attribute’s *presence* means subtitles should be on
    const subtitlesEnabled = this.el.hasAttribute("defaultsubtitles");

    if (!subtitlesEnabled) {
      const captionsMenu = this.el.querySelector("media-captions-menu");
      if (!captionsMenu || !captionsMenu.shadowRoot) return; // menu not ready yet

      const menuItems = captionsMenu.shadowRoot.querySelectorAll(
        "media-chrome-menu-item",
      );

      // First item in the list is usually “Off”
      if (menuItems.length) menuItems[0].click();
    }
  },

  highlightSegment({ index }) {
    this.highlightedSegmentIndex = index;
    const timeRange = this.el.querySelector("media-time-range");
    timeRange.mediaChaptersCues = this.chapters;

    // Set highlighted segment for playlist item
    if (this.highlightedSegmentIndex) {
      const rect = timeRange?.shadowRoot?.querySelector(
        `#segments-clipping rect:nth-child(${
          this.highlightedSegmentIndex + 1
        })`,
      );

      if (rect) {
        rect.style.height = "10px";
        rect.style.y = "0%";
      }
    }
  },

  forceChapters() {
    if (
      (!this.chapters ||
        this.chapters == [] ||
        this.lastLoadedVideoId != this.video.dataset.id) &&
      !this.loadingChapters
    ) {
      this.loadingChapters = true;
      this.lastLoadedVideoId = this.video.dataset.id;
      fetch(`${appBasePath}/asset/chapters/${this.video.dataset.slug}${this.video.dataset.id}?fly_prefer_instance_id=${flyInstanceId}`)
        .then((response) => response.text())
        .then((data) => {
          const lines = data.split("\n");
          const chapters = [];

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();

            if (!line || line === "WEBVTT") continue;

            if (line.includes("-->")) {
              const [startTime, endTime] = line
                .split("-->")
                .map((time) => time.trim());

              const chapterTitle = lines[++i]?.trim();

              if (!chapterTitle) continue;

              chapters.push({
                text: chapterTitle,
                startTime: toSeconds(startTime),
                endTime: toSeconds(endTime),
              });
            }
          }

          function toSeconds(timeString) {
            const [hours, minutes, seconds] = timeString
              .split(":")
              .map(parseFloat);
            return Math.floor(hours * 3600 + minutes * 60 + seconds);
          }

          this.loadingChapters = null;
          this.chapters = chapters;
        })
        .catch((error) => console.error("Error parsing chapters:", error));
    }

    // Force attaching state again
    this.el.connectedCallback();

    // Workaround for media-chrome bug to set chapters
    setTimeout(() => {
      this.highlightSegment({ index: this.highlightedSegmentIndex });
    }, 500);
  },

  unmuteVideo(event) {
    if (this.noPlayerUI) return;

    if (
      this.video.muted &&
      !this.el.querySelector("#autoplay-muted").classList.contains("hidden")
    ) {
      this.video.muted = false;
      if (this.video.paused) this.video.play();
      this.hideMutedBadge();
      event.stopPropagation();
      event.preventDefault();
    }
  },

  showMutedBadge() {
    if (this.noPlayerUI) return;

    const mutedBadge = this.el.querySelector("#autoplay-muted");
    if (!mutedBadge) return;
    mutedBadge.classList.remove("hidden");
  },

  hideMutedBadge() {
    const mutedBadge = this.el.querySelector("#autoplay-muted");
    if (!mutedBadge) return;
    mutedBadge.classList.add("hidden");
  },

  cueChange(textTrack) {
    // Get the active cues
    var cues = textTrack.activeCues;

    // If there are active cues, display the subtitle text
    if (cues && cues.length > 0) { // https://appsignal.com/plum-village/sites/671b765cd2a5e408f7609a40/exceptions/incidents/91/samples/671b765cd2a5e408f7609a40-645978896882031538417728106401 null is not an object (evaluating 'e.length')
      this.subtitle.style.opacity = 1;
      this.subtitle.style.transform = "scale(1)";
      this.subtitleInner.innerHTML = cues[0].text.replace(/\n/g, "<br>");
    } else {
      // If no active cues, clear the subtitle display
      this.subtitle.style.opacity = 0;
      this.subtitle.style.transform = "scale(0.99)";
    }
  },

  reloadSubtitles() {
    const tracks = this.video.querySelectorAll("track");

    tracks.forEach((track) => {
      // Clone the current track
      const newTrack = track.cloneNode();

      // Remove the old track
      track.remove();

      // Append the cloned track back to the video element
      this.video.appendChild(newTrack);
    });

    // Firefox-specific fix: Force media-chrome to reload thumbnail and chapters tracks
    const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    if (isFirefox) {
      setTimeout(() => {
        // Force metadata and chapters tracks to load
        Array.from(this.video.textTracks).forEach((track) => {
          if (
            (track.kind === "metadata" && track.label === "thumbnails") ||
            track.kind === "chapters"
          ) {
            track.mode = "hidden";
          }
        });
      }, 100);
    }
  },

  keyboardShortcuts(event) {
    const tagName = event.target.tagName.toLowerCase();
    const isEditable = event.target.contentEditable === "true";
    const isInteractiveElement = ["select", "input", "textarea"].includes(
      tagName,
    );
    const hasTabIndex = event.target.hasAttribute("tabindex");

    // Special case: Allow Shift+Meta+Space to control video even when in editable elements
    if (event.code === "Space" && event.shiftKey && event.metaKey) {
      this.video.paused ? this.video.play() : this.video.pause();
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    // esc can also play / pause
    if ((isEditable || isInteractiveElement) && event.code === "Escape") {
      this.video.paused ? this.video.play() : this.video.pause();
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    // Don't handle other keyboard shortcuts when in editable elements
    if (isEditable || isInteractiveElement) {
      return;
    }

    // Check if modifier keys are pressed, for instance cmd + F
    if (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) {
      return;
    }

    // Prevent space bar from triggering scroll or play/pause when button is selected
    if (event.code === "Space" && hasTabIndex) {
      event.preventDefault();
      return;
    }

    switch (event.code) {
      case "Space":
      case "KeyK":
        this.video.paused ? this.video.play() : this.video.pause();
        break;
      case "ArrowRight":
        this.video.currentTime += 10;
        break;
      case "ArrowLeft":
        this.video.currentTime -= 10;
        break;
      case "KeyM":
        this.video.muted = !this.video.muted;
        break;
      case "KeyF":
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          this.el.requestFullscreen();
        }
        break;
      case "KeyC":
        var subtitlesVisible = false;
        for (let i = 0; i < this.video.textTracks.length; i++) {
          const track = this.video.textTracks[i];
          if (track.kind === "subtitles" && track.mode === "showing") {
            subtitlesVisible = true;
            track.mode = "disabled";
          }
        }
        if (!subtitlesVisible) {
          const track = this.video.textTracks[0];
          track.mode = "showing";
        }

        break;
      default:
        return;
    }

    event.stopPropagation();
    event.preventDefault();
  },
  destroyed() {
    document.removeEventListener("visibilitychange", this.boundVisibilityRefresh);
    if (this.boundKeyboardShortcuts) {
      window.removeEventListener("keydown", this.boundKeyboardShortcuts);
    }
    this.video?.removeEventListener("seeking", this.boundHandlePreviewSeek);
    this.video?.removeEventListener("seeked", this.boundHandlePreviewSeek);

    if (this.boundRestartPlayback) {
      this.video?.removeEventListener("ended", this.boundRestartPlayback);
    }

    // Clean up HLS instance when hook is destroyed
    this.teardownRenditionBridge();
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  },
  hideControls() {
    // Reset any existing timeout to prevent multiple hide events
    if (hideControlsTimeout) {
      clearTimeout(hideControlsTimeout);
    }

    // Add a delay before hiding controls
    hideControlsTimeout = setTimeout(() => {
      // Dispatch a mouseleave event on the video element to hide controls
      const event = new MouseEvent("mouseleave", {
        bubbles: true,
        cancelable: true,
      });
      this.video.dispatchEvent(event);
      hideControlsTimeout = null;
    }, 3000); // 3 second delay to allow for multiple clicks
  },

  disconnected() {
    this.disconnectedTime = this.video?.currentTime ?? 0;
    this.disconnectedPlaying = this.video ? !this.video.paused : false;

    // Keep tracking state while video continues playing during disconnect, so we can restore to the right time and play/pause state when reconnected
    this.disconnectedStateTracker = () => {
      this.disconnectedTime = this.video?.currentTime ?? this.disconnectedTime;
      this.disconnectedPlaying = this.video ? !this.video.paused : this.disconnectedPlaying;
    };
    this.video?.addEventListener("timeupdate", this.disconnectedStateTracker);
    this.video?.addEventListener("pause", this.disconnectedStateTracker);
    this.video?.addEventListener("play", this.disconnectedStateTracker);
  },

  reconnected() {
    if (this.disconnectedStateTracker) {
      this.video?.removeEventListener("timeupdate", this.disconnectedStateTracker);
      this.video?.removeEventListener("pause", this.disconnectedStateTracker);
      this.video?.removeEventListener("play", this.disconnectedStateTracker);
    }

    const savedTime = this.disconnectedTime ?? 0;
    const wasPlaying = this.disconnectedPlaying ?? false;
    const wasFullscreen = !!document.fullscreenElement;

    this.previewLoadStoppedBy403 = false;
    this.previewShouldResumeAfterForbidden = false;

    if (this.disconnectedStateTracker) {
      this.skipServerVideoTime = true; //because the video keeps playing after the disconnect, now the server ("resume where you left off") has slightly stale time, compared to what we have. So ignore the server's time and use our own.
    }

    const originalSrc = this.video.getAttribute("data-original-src"); //hls.js modifies the value in the src attribute to be a blob: url (and somehow liveview patch leaves it), so here we need to use the src that it "should" be
    if (!Hls.isSupported()) {
      alert("Your browser or device is too old to view talks on Living Gems. Please update Safari to version 17.1 or later.");
      return;
    }
    if (originalSrc) {
      // destroy old hls instance if it exists, because it's now stale (referring to data in the video element which liveview may have patched). this used to be in disconnected(), which would break the video as soon as internet dropped.
      this.teardownRenditionBridge();
      if (this.hls) {
        this.hls.destroy();
      }

      this.hls = new Hls(this.getHlsConfig());
      this.hls.loadSource(originalSrc);
      this.hls.attachMedia(this.video);
      this.attachPlaybackReporting();
      this.attachPreviewHlsErrorHandler();
      this.removeMismatchedFrameRateLevels();
      this.capAutoLevelTo1080p();
      this.setupRenditionBridge();

      if (this.disconnectedStateTracker) {
        this.video.currentTime = savedTime;
      } else {
        this.seekToPreviewStartWhenReady();
      }

      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setTimeout(() => {
          if (wasPlaying) {
            this.video.play();
          } else {
            this.video.pause();
          }

          // Re-sync media-chrome's fullscreen button — it loses track when HLS is re-initialized.
          if (wasFullscreen) {
            this.el.setAttribute("mediaisfullscreen", "");
            this.el.querySelector("media-fullscreen-button")?.setAttribute("mediaisfullscreen", "");
          }
        }, 500);
      });
    } else {
      setTimeout(() => {
        this.video.pause();
      }, 500);
    }

    if (this.disconnectedStateTracker) {
      this.disconnectedStateTracker = null;
    }
  },
};
